import { FunnelId } from 'constants/funnel';
import PackagesContainer from 'components/Pages/DoctorProfile/PackagesContainer';
import ConversionBar from 'components/Shared/ConversionBar';
import Reviews from 'components/Shared/Reviews/Reviews';
import Icon from 'components/ui/Icon';
import { replaceSpecialChar } from 'utils/string';
import { useTranslation } from 'hooks/useTranslation';
import About from './About';
import BeforeAfter from './BeforeAfter';
import ClinicPhotos from './ClinicPhotos';
import Hero from './Hero';
import StructuredData from './StructuredData';
import Team from './Team';
import Testimonials from './Testimonials';
import type { ReviewItem, ReviewStats } from 'types/Review';
import type { ClinicPage as ClinicPageType } from 'types/Clinic';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';

type Props = {
  entry: ClinicPageType;
  bestReview: Record<string, string>;
  reviewStats: ReviewStats;
  resultState: ReviewItem[];
  packages: Record<string, unknown>[];
  beforeAfterPhotos?: APIBeforeAfterPhoto[];
  baPhotosTotalCount?: string | number;
};

const ClinicPage = ({
  entry,
  reviewStats,
  resultState,
  beforeAfterPhotos,
  baPhotosTotalCount,
  bestReview,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const funnelId = entry.funnel ? entry.funnel.sys.id : FunnelId.CONTACT_FORM;

  const customFieldOverrides: Record<string, unknown> = {
    clinicName: entry.name,
    slug: entry.slug,
    isClinicPage: true,
  };

  const clinicTreatments = entry.clinicTreatment?.items;
  if (clinicTreatments?.length) {
    clinicTreatments?.filter(Boolean);
  }

  const doctors = entry.doctors?.items;
  const photos = entry.photos?.items;
  const videos = entry.videos?.items;

  return (
    <>
      <StructuredData
        entry={entry}
        bestReview={bestReview}
        reviewStats={reviewStats}
      />
      <Hero
        clinic={entry}
        reviewStats={reviewStats}
        customFieldOverrides={customFieldOverrides}
        funnelId={funnelId}
      />
      <div className="lg:pt-18">
        <About clinic={entry} />
        {!!resultState?.length && (
          <div
            id="reviews"
            className="border-t border-silver-light md:pt-18 mb-20"
          >
            <div className="max-w-screen-xl mx-auto px-0">
              <Reviews
                stats={reviewStats}
                reviews={resultState}
                title={`${entry.name} ${t('reviews')}`}
                noGrid
              />
            </div>
          </div>
        )}
        {!!photos.length && <ClinicPhotos photos={photos} />}
        {!!videos?.length && <Testimonials videos={videos} />}
        {!!beforeAfterPhotos?.length && (
          <BeforeAfter
            beforeAfterPhotos={beforeAfterPhotos}
            clinic={entry}
            baPhotosTotalCount={baPhotosTotalCount}
          />
        )}
        {!!doctors?.length && <Team doctors={doctors} />}
        {!!clinicTreatments?.length && (
          <div
            id="packages"
            className={reviewStats && resultState ? '' : 'mb-20'}
          >
            <PackagesContainer
              entry={clinicTreatments}
              itineraryText={entry.itineraryText}
              accommodation={entry?.defaultAccommodation}
            />
          </div>
        )}
      </div>
      <ConversionBar hideContactButton funnelId={funnelId}>
        <div className="w-2/5 md:w-full">
          <div className="md:flex items-center">
            <p className="text-navy font-bold truncate whitespace-nowrap">
              {replaceSpecialChar(entry.name)}
            </p>
            <div>
              {reviewStats &&
                !!reviewStats.avg_rating &&
                !!reviewStats.total && (
                  <p className="flex items-center md:ml-4">
                    <span className="font-bold mr-1 text-tertiary md:text-sm order-2 md:order-1">
                      {reviewStats.avg_rating.toFixed(1)}
                    </span>{' '}
                    <Icon type="Star" className="mr-2 order-1 md:order-2" />
                    <span className="md:text-xs text-charcoal md:text-qunoGrey order-3">
                      ({reviewStats.total})
                    </span>
                  </p>
                )}
            </div>
          </div>
        </div>
      </ConversionBar>
    </>
  );
};

export default ClinicPage;
